import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import PhotoUpload from "./PhotoUpload";
import { CheckCircle2, Send } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const LeadForm = () => {
  const [form, setForm] = useState({
    customer_name: "",
    customer_whatsapp: "",
    location: "",
    description: "",
    interior_exterior: "interior",
    timeline: "",
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_whatsapp || !form.location || !form.description) {
      toast.error("Please fill in your name, WhatsApp, location and what you need");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        photos: photos.map((p) => p.url),
      };
      const res = await api.post("/public/leads", payload);
      setSubmitted(res.data);
      trackEvent("lead_form_submitted", { matched_count: res.data.matched_count, has_photos: photos.length > 0 });
      toast.success(`Done! We matched you with ${res.data.matched_count} painters.`);
    } catch (err) {
      toast.error("Couldn't submit. Try again or use WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-[#25D366]/30 rounded-2xl p-8 text-center" data-testid="lead-submitted-card">
        <div className="w-16 h-16 mx-auto bg-[#25D366]/10 rounded-full flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-[#25D366]" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-[#2D3748] mt-4">You're matched!</h3>
        <p className="mt-2 text-[#718096]">
          We sent your request to <b>{submitted.matched_count}</b> trusted painter{submitted.matched_count === 1 ? "" : "s"} in {form.location}. You'll receive quotes on WhatsApp within a few hours.
        </p>
        <Button
          onClick={() => { setSubmitted(null); setPhotos([]); setForm({ customer_name: "", customer_whatsapp: "", location: "", description: "", interior_exterior: "interior", timeline: "" }); }}
          variant="outline"
          className="mt-6 rounded-full"
          data-testid="lead-new-request"
        >
          New request
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 md:p-8 space-y-5" data-testid="lead-form">
      <div>
        <h3 className="font-heading text-2xl font-bold text-[#2D3748]">Tell us about the job</h3>
        <p className="text-sm text-[#718096] mt-1">Photos help painters quote accurately — and faster.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cn">Your name</Label>
          <Input id="cn" data-testid="lf-name" value={form.customer_name} onChange={update("customer_name")} placeholder="Nomvula" className="mt-1.5 rounded-xl" required />
        </div>
        <div>
          <Label htmlFor="wa">WhatsApp number</Label>
          <Input id="wa" data-testid="lf-wa" value={form.customer_whatsapp} onChange={update("customer_whatsapp")} placeholder="+27 84 111 2200" className="mt-1.5 rounded-xl" required />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="loc">Suburb / city</Label>
          <Input id="loc" data-testid="lf-loc" value={form.location} onChange={update("location")} placeholder="Durbanville" className="mt-1.5 rounded-xl" required />
        </div>
        <div>
          <Label htmlFor="ie">Interior or exterior?</Label>
          <select
            id="ie"
            data-testid="lf-ie"
            value={form.interior_exterior}
            onChange={update("interior_exterior")}
            className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 bg-white outline-none focus:border-[#25D366] h-10"
          >
            <option value="interior">Interior</option>
            <option value="exterior">Exterior</option>
            <option value="both">Both</option>
          </select>
        </div>
      </div>
      <div>
        <Label htmlFor="desc">What needs painting?</Label>
        <textarea
          id="desc"
          data-testid="lf-desc"
          value={form.description}
          onChange={update("description")}
          rows={3}
          placeholder="Repaint living room and 2 bedrooms, ~60m², neutral colours"
          className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 outline-none focus:border-[#25D366]"
          required
        />
      </div>
      <div>
        <Label htmlFor="tl">Timeline</Label>
        <Input id="tl" data-testid="lf-tl" value={form.timeline} onChange={update("timeline")} placeholder="Next week, this month, flexible…" className="mt-1.5 rounded-xl" />
      </div>
      <PhotoUpload value={photos} onChange={setPhotos} />
      <Button
        type="submit"
        disabled={submitting}
        data-testid="lead-form-submit"
        className="w-full rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white py-6 font-medium wazi-cta-glow text-base"
      >
        <Send className="w-4 h-4 mr-2" /> {submitting ? "Sending…" : "Get matched with painters"}
      </Button>
      <p className="text-xs text-[#718096] text-center">
        By submitting, you agree to receive WhatsApp quotes from matched painters. We never share your number with non-painters.
      </p>
    </form>
  );
};

export default LeadForm;
